import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import axios from 'axios';

const BASE_URL = 'https://whapi.infosware-test.in';

function EmployeeForm({ employee, onCancel, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        password: '',
        confirmpassword: '',
        permissions: [] // New field for permissions
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (employee) {
            // Populate the form fields with employee data
            setFormData({
                name: employee.name,
                username: employee.username,
                password: employee.password, // Do not show the password in plain text (for security)
                confirmpassword: employee.password, // Same for confirm password
                permissions: employee.permissions || [] // Populate permissions
            });
        }
    }, [employee]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value.trim() });
        setFieldErrors({ ...fieldErrors, [name]: '' });
    };

    const handlePermissionChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prevState) => {
            const permissions = Array.isArray(prevState.permissions) ? prevState.permissions : [];
            const updatedPermissions = checked
                ? [...permissions, value]
                : permissions.filter((perm) => perm !== value);
            return { ...prevState, permissions: updatedPermissions };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        const errors = {};
        if (!formData.name) errors.name = 'Name is required.';
        if (!formData.username) errors.username = 'Username is required.';
        if (!formData.password) errors.password = 'Password is required.';
        if (!formData.confirmpassword) errors.confirmpassword = 'Confirm Password is required.';
        if (formData.password !== formData.confirmpassword) errors.confirmpassword = 'Passwords do not match.';

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setIsLoading(false);
            return;
        }

        setError('');
        const url = employee ? `${BASE_URL}/edit-employee/${employee.id}` : `${BASE_URL}/add-employees`;
        const method = employee ? 'put' : 'post';

        axios[method](url, {
            name: formData.name,
            username: formData.username,
            password: formData.password,
            permissions: formData.permissions // Include permissions in the request
        })
            .then((response) => {
                setSuccessMessage(employee ? 'Employee updated successfully!' : 'Employee added successfully!');
                onSave(response.data); // This will trigger the parent component to refresh the list and go back to the list view
                setFormData({
                    name: '',
                    username: '',
                    password: '',
                    confirmpassword: '',
                    permissions: []
                });
            })
            .catch((error) => {
                console.error("There was an error!", error);
                setError("Error saving employee. Please try again.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div>
            <h3 className='mt-1 p-4 bg-secondary text-white'>{employee ? 'Edit Employee' : 'New Employee'}</h3>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
            <Form className='container p-4 mb-3 shadow-sm rounded bg-white' onSubmit={handleSubmit}>
                <Form.Group controlId="name">
                    <Form.Label>Name :</Form.Label>
                    <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                    {fieldErrors.name && <p style={{ color: 'red' }}>{fieldErrors.name}</p>}
                </Form.Group>

                <Form.Group controlId="username">
                    <Form.Label>Username :</Form.Label>
                    <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />
                    {fieldErrors.username && <p style={{ color: 'red' }}>{fieldErrors.username}</p>}
                </Form.Group>

                <Form.Group className='mt-2' controlId="permissions">
                    <Form.Label><strong>Permissions :</strong></Form.Label>
                    <div>
                        <Form.Check
                            type="checkbox"
                            id="permission-home"
                            label="Access Home"
                            value="home"
                            checked={formData.permissions.includes("home")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-employees"
                            label="Access Employees"
                            value="employees"
                            checked={formData.permissions.includes("employees")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-items"
                            label="Access Items"
                            value="items"
                            checked={formData.permissions.includes("items")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-registertag"
                            label="Access Register Tags"
                            value="registertag"
                            checked={formData.permissions.includes("registertag")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-warehouse"
                            label="Access WareHouse"
                            value="warehouse"
                            checked={formData.permissions.includes("warehouse")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-reports"
                            label="Access Reports"
                            value="reports"
                            checked={formData.permissions.includes("reports")}
                            onChange={handlePermissionChange}
                        />
                        <Form.Check
                            type="checkbox"
                            id="permission-scan"
                            label="Access Scan"
                            value="scan"
                            checked={formData.permissions.includes("scan")}
                            onChange={handlePermissionChange}
                        />
                    </div>
                </Form.Group>

                {/* Show password and confirm password fields when editing an employee */}
                {!employee ? (
                    <>
                        <Form.Group controlId="password">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            {fieldErrors.password && <p style={{ color: 'red' }}>{fieldErrors.password}</p>}
                        </Form.Group>

                        <Form.Group controlId="confirmpassword">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmpassword"
                                value={formData.confirmpassword}
                                onChange={handleChange}
                                required
                            />
                            {fieldErrors.confirmpassword && <p style={{ color: 'red' }}>{fieldErrors.confirmpassword}</p>}
                        </Form.Group>
                    </>
                ) : (
                    <>
                        <Form.Group controlId="password">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                placeholder="Leave empty to keep the same password"
                                value={formData.password}
                                onChange={handleChange}
                            />
                            {fieldErrors.password && <p style={{ color: 'red' }}>{fieldErrors.password}</p>}
                        </Form.Group>

                        <Form.Group controlId="confirmpassword">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmpassword"
                                placeholder="Leave empty to keep the same password"
                                value={formData.confirmpassword}
                                onChange={handleChange}
                            />
                            {fieldErrors.confirmpassword && <p style={{ color: 'red' }}>{fieldErrors.confirmpassword}</p>}
                        </Form.Group>
                    </>
                )}

                <Button className='mt-3' variant="danger" onClick={onCancel}>Back</Button>{' '}
                <Button className='mt-3' variant="primary" type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                </Button>
            </Form>
        </div>
    );
}

export default EmployeeForm;
